import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, ViewChild, effect } from '@angular/core';
import { DataEmptyAnimationComponent } from 'app/components/distinct-components/animations/data-animations/data-empty-state/data-empty-animation.component';
import { FlightLocationComponent } from 'app/components/distinct-components/flight/flight-location/flight-location.component';
import { FlightPrimaryDataComponent } from 'app/components/distinct-components/flight/flight-primary-data/flight-primary-data.component';
import { FlightSecondaryDataComponent } from 'app/components/distinct-components/flight/flight-secondary-data/flight-secondary-data.component';
import { SvgLoaderComponent } from 'app/components/ui-kit/images/svg-loader/svg-loader.component';
import { AppStateService } from 'app/services/app-state.service';
import { TimeAndDateService } from 'app/services/time-and-date-service';
import anime from 'animejs/lib/anime.es.js';
import { delay } from 'rxjs';

@Component({
  selector: 'app-agent-data-card-inline',
  standalone: true,
  imports: [CommonModule, SvgLoaderComponent, DataEmptyAnimationComponent, FlightLocationComponent, FlightPrimaryDataComponent, FlightSecondaryDataComponent],
  templateUrl: './agent-data-card-inline.component.html',
  styleUrl: './agent-data-card-inline.component.scss'
})
export class AgentDataCardInlineComponent {
  @ViewChild('cardContainer') cardContainer!: ElementRef; //powerAnimateContainer
  @Input() currentDataModel: any;

  cardInitialized: boolean = false;
  private isAnimating = false;
  private animationTimeout: any = null;

  private animationStartedAt: number | null = null;
  private minAnimationDuration = 1800; // in ms (adjust as needed)


  private animationTimeline: anime.AnimeTimelineInstance | null = null;

  constructor(private timeAndDate: TimeAndDateService, public appState: AppStateService) {
    effect(() => {
      const isSearching = this.appState.agent.isSearchingFlights();
      if (isSearching && this.cardInitialized) {
        this.isAnimating = true;
        setTimeout(() => {
          this.initAnimation();
        }, 1000);
      } else {
        setTimeout(() => {
          this.stopAnimation();
        }, 600);
      }
    });
  }

  ngAfterViewInit() {
    this.cardInitialized = true;
  }

  private initAnimation(): void {
    if (!this.cardContainer || !this.cardContainer.nativeElement) {
      console.error('cardContainer is not available.');
      return;
    }

    const containerWidth = this.cardContainer.nativeElement.offsetWidth;
    const containerHeight = this.cardContainer.nativeElement.offsetHeight;
    const ballElement = document.querySelector('.ball') as HTMLElement | null;

    if (!this.isAnimating || !ballElement) return;

    anime.remove('.ball');
    ballElement.classList.add('active');
    this.animationStartedAt = Date.now();

    this.animationTimeline = anime.timeline({
      targets: '.ball',
      easing: 'easeInOutSine',
      autoplay: true,
      loop: true,
      update: (anim) => {
        const targets = anim.animatables[0].target as HTMLElement;
        const offset = (anim.currentTime % 4000) / 4000;
        const shadowLength = 64 * offset;
        targets.style.boxShadow = `0 0 ${shadowLength}px rgb(255 245 190 / 70%)`;
      }
    });

    this.animationTimeline
      .add({ translateY: -8, duration: 10 })
      .add({ translateX: containerWidth - 2, duration: 600 })
      .add({ translateY: containerHeight, duration: 300 })
      .add({ translateX: -6, duration: 600 })
      .add({ translateY: 0, duration: 300 });
  }


  private stopAnimation(): void {
    const now = Date.now();
  
    if (
      this.animationStartedAt &&
      now - this.animationStartedAt < this.minAnimationDuration
    ) {
      const delay = this.minAnimationDuration - (now - this.animationStartedAt);
      setTimeout(() => this.stopAnimation(), delay);
      return;
    }
  
    this.isAnimating = false;
    this.animationStartedAt = null;
  
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
      this.animationTimeout = null;
    }
  
    if (this.animationTimeline) {
      this.animationTimeline.pause();
      this.animationTimeline.seek(0);
      this.animationTimeline = null;
    }
  
    const ballElement = document.querySelector('.ball') as HTMLElement | null;
    if (ballElement) {
      ballElement.classList.remove('active');
      ballElement.style.boxShadow = 'none';
      ballElement.style.transform = 'translate(0px, 0px)';
    }
  
    anime.remove('.ball');
  }
  

  getDateLabel(): string {
    const timing = this.currentDataModel.travelTiming;
    const isExact = timing.departureDateExact;
    const isRange = timing.departureDateRange?.start;
    const isExisting = this.currentDataModel.isExistingReservation;

    if (!isExact && !isRange) {
      return isExisting ? 'When are you going' : 'Departing when?';
    }

    return isExact ? 'Departing on:' : 'Any Day Between:';
  }

  getDateValue(): string | null {
    const timing = this.currentDataModel.travelTiming;
    const exact = timing.departureDateExact;
    const rangeStart = timing.departureDateRange?.start;
    const rangeEnd = timing.departureDateRange?.end;

    if (!exact && !rangeStart) {
      return null;
    }

    if (exact) {
      return this.timeAndDate.formatDateToMMDDYY(exact);
    }

    return `${this.timeAndDate.formatDateToMMDDYY(rangeStart)} - ${this.timeAndDate.formatDateToMMDDYY(rangeEnd)}`;
  }

  getTimeLabel(): string {
    const timing = this.currentDataModel.travelTiming;
    const tz = this.timeAndDate.getTimezoneAbbreviation(timing.timeZone);
    if (timing.departureTimeExact) {
      return `Departing ( ${tz} )`;
    } else if (this.isAnytimeRange(timing.departureTimeRange?.start, timing.departureTimeRange?.end)) {
      return `Departure time ( ${tz} )`;
    } else if (timing.departureTimeRange?.start) {
      return `Between ( ${tz} )`;
    }
    return 'What time of day?';
  }

  getTimeValue(): string | null {
    const timing = this.currentDataModel.travelTiming;

    if (timing.departureTimeExact) {
      const time = this.timeAndDate.formatTimeForTimezone(timing.timeZone, timing.departureTimeExact);
      return `${time}`;
    }

    const start = timing.departureTimeRange?.start;
    const end = timing.departureTimeRange?.end;

    if (start && end) {
      if (this.isAnytimeRange(start, end)) {
        return 'Any time';
      }
      const startTime = this.timeAndDate.formatTimeForTimezone(timing.timeZone, start);
      const endTime = this.timeAndDate.formatTimeForTimezone(timing.timeZone, end);
      return `${startTime} - ${endTime}`;
    }

    return null;
  }

  private isAnytimeRange(start: string | null | undefined, end: string | null | undefined): boolean {
    return start === '00:00:00' && (end === '23:59:00' || end === '23:59:59');
  }

  getSeatsLabel(): string {
    const seatsOffered = this.currentDataModel.reservationData.seatsOffered;
    const seatsNeeded = this.currentDataModel.seatsNeeded;

    if (!seatsNeeded && !seatsOffered) {
      return 'How many seats?';
    } else {
      return 'Number of Seats';
    }
  }

  getSeatsValue(): string | null {
    const seatsOffered = this.currentDataModel.reservationData.seatsOffered;
    const seatsNeeded = this.currentDataModel.seatsNeeded;

    if (!seatsNeeded && !seatsOffered) {
      return null;
    } else {
      return seatsNeeded ? `${seatsNeeded} Seats Needed` : `${seatsOffered} Seats Available`;
    }
  }

  onClickDataCard() {
    if (this.appState.agent.showInputAssist()) {
      return;
    }
    this.appState.agent.showInputAssist.set(true);
    setTimeout(() => {
      this.appState.agent.showInputAssist.set(false);
    }, 4000);
  }

}
